import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Divider,
    TextField,
    Paper,
    Badge,
    CircularProgress
} from '@mui/material';
import {
    Close as CloseIcon,
    Chat as ChatIcon,
    Send as SendIcon,
    ArrowBack as ArrowBackIcon,
    DoneAll as DoneAllIcon
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { coreFeatureService } from '../../services/coreFeatureService';
import { useChat } from '../../contexts/ChatContext';
import { blobticRealtimeService } from '../../services/blobticRealtimeService';

interface ChatDrawerProps {
    open: boolean;
    onClose: () => void;
}

interface ChatUser {
    id: number;
    name: string;
    avatar?: string;
    online: boolean;
}

interface ChatMessage {
    id: number;
    text: string;
    senderId: number;
    timestamp: Date;
    isOwn: boolean;
    isRead: boolean;
}

const PAGE_SIZE = 20;

const ChatDrawer: React.FC<ChatDrawerProps> = ({ open, onClose }) => {
    const { t } = useLanguage();
    const { user } = useAuth();
    const { unreadCounts, markAsRead } = useChat();
    const [activeChat, setActiveChat] = useState<ChatUser | null>(null);
    const [messageInput, setMessageInput] = useState('');
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [users, setUsers] = useState<ChatUser[]>([]);

    // Pagination states
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const prevScrollHeightRef = useRef<number>(0);

    const loadUsers = async () => {
        try {
            const rawUsers = await coreFeatureService.getChatUsers();
            if (rawUsers && user) {
                const mappedUsers = rawUsers
                    .filter((u: any) => Number(u.USR_IDF) !== Number(user.id)) // Robustly filter out self
                    .map((u: any) => ({
                        id: u.USR_IDF,
                        name: u.USR_LIB,
                        online: !!u.IS_ONLINE
                    }));
                setUsers(mappedUsers);
            }
        } catch (e) {
            console.error("Failed to load chat users", e);
        }
    };

    /**
     * Load messages for a specific page. 
     * If page 0 (initial load/refresh), we replace messages.
     * If page > 0 (load more), we prepend messages.
     */
    const loadMessages = useCallback(async (userId: number, pageNum: number) => {
        try {
            if (pageNum > 0) setLoadingMore(true);

            // Fetch generic latest messages. Backend returns DESC order due to pagination params.
            const msgs = await coreFeatureService.getChatMessages(userId, pageNum, PAGE_SIZE);

            const processedMsgs = msgs.map(m => ({
                id: m.CHT_UIDF,
                text: m.CHT_MESSAGE,
                senderId: m.CHT_SENDER_ID,
                timestamp: new Date(m.CHT_DATE),
                isOwn: m.CHT_SENDER_ID !== userId, // If I am not sender (msg sender != my partner), wait.
                isRead: m.CHT_IS_READ
            }));

            // Since backend returns DESC order (Newest First), we should reverse them
            // to display correctly (Oldest at top, Newest at bottom) within the chunk.
            processedMsgs.reverse();

            setMessages(prev => {
                if (pageNum === 0) return processedMsgs;
                return [...processedMsgs, ...prev];
            });

            setHasMore(msgs.length === PAGE_SIZE);
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingMore(false);
        }
    }, []);

    useEffect(() => {
        if (open) {
            loadUsers();

            const unsubscribe = blobticRealtimeService.subscribe((event) => {
                const obj = (event.objectName || '').toLowerCase();
                if (obj === 'chat_message' || obj === 'sys_chat' || obj === 'tabp_chat_message') {
                    loadUsers();
                    if (activeChat) {
                        loadMessages(activeChat.id, 0);
                        markAsRead(activeChat.id);
                    }
                }
            });

            return () => {
                unsubscribe();
            };
        }
    }, [open, activeChat, loadMessages, markAsRead]);

    // Reset when opening a chat
    useEffect(() => {
        if (activeChat) {
            setMessages([]);
            setPage(0);
            setHasMore(true);
            loadMessages(activeChat.id, 0);
            markAsRead(activeChat.id);
        }
    }, [activeChat, loadMessages, markAsRead]);

    // Scroll handling for "Load More"
    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const scrollTop = e.currentTarget.scrollTop;
        if (scrollTop === 0 && hasMore && !loadingMore) {
            // User hit top, load next page
            const nextPage = page + 1;
            setPage(nextPage);
            // Record scroll height before load to restore position
            if (scrollContainerRef.current) {
                prevScrollHeightRef.current = scrollContainerRef.current.scrollHeight;
            }
            loadMessages(activeChat!.id, nextPage);
        }
    };

    // Restore scroll position after loading more
    useEffect(() => {
        if (loadingMore === false && page > 0 && scrollContainerRef.current) {
            // difference in height
            const newScrollHeight = scrollContainerRef.current.scrollHeight;
            const diff = newScrollHeight - prevScrollHeightRef.current;
            scrollContainerRef.current.scrollTop = diff;
        } else if (page === 0 && scrollContainerRef.current) {
            // Initial load, scroll to bottom
            scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
        }
    }, [messages, loadingMore, page]);

    const handleUserClick = (user: ChatUser) => {
        setActiveChat(user);
    };

    const handleBackInfo = () => {
        setActiveChat(null);
        setMessages([]);
        setPage(0);
    };

    const handleSend = async () => {
        if (!messageInput.trim() || !activeChat) return;

        const success = await coreFeatureService.sendMessage(activeChat.id, messageInput);
        if (success) {
            setMessageInput('');
            // Reload page 0 (latest)
            setPage(0);
            loadMessages(activeChat.id, 0);
        }
    };

    // Helper for date separators
    const getFormattedDateLabel = (date: Date) => {
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        const isToday = date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear();

        const isYesterday = date.getDate() === yesterday.getDate() &&
            date.getMonth() === yesterday.getMonth() &&
            date.getFullYear() === yesterday.getFullYear();

        if (isToday) return t('common.date.today') || "Today";
        if (isYesterday) return t('common.date.yesterday') || "Yesterday";
        return date.toLocaleDateString();
    };

    const renderMessagesWithDateSeparators = () => {
        const result: React.ReactNode[] = [];
        let lastDateLabel: string | null = null;

        messages.forEach((msg) => {
            const dateLabel = getFormattedDateLabel(msg.timestamp);

            if (dateLabel !== lastDateLabel) {
                result.push(
                    <Box key={`date-${dateLabel}-${msg.id}`} sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                        <Typography variant="caption" sx={{ bgcolor: 'action.hover', px: 1, py: 0.5, borderRadius: 1, color: 'text.secondary' }}>
                            {dateLabel}
                        </Typography>
                    </Box>
                );
                lastDateLabel = dateLabel;
            }
            result.push(
                <Box
                    key={msg.id}
                    sx={{
                        alignSelf: msg.isOwn ? 'flex-end' : 'flex-start',
                        maxWidth: '80%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: msg.isOwn ? 'flex-end' : 'flex-start'
                    }}
                >
                    <Paper
                        elevation={0}
                        sx={{
                            p: 1.5,
                            bgcolor: msg.isOwn ? 'primary.main' : 'background.paper',
                            color: msg.isOwn ? 'primary.contrastText' : 'text.primary',
                            borderRadius: 2,
                            borderTopRightRadius: msg.isOwn ? 0 : 2,
                            borderTopLeftRadius: msg.isOwn ? 2 : 0,
                            position: 'relative',
                            minWidth: msg.isOwn ? '120px' : 'auto'
                        }}
                    >
                        <Typography variant="body2" sx={{ pr: msg.isOwn ? 2 : 0 }}>{msg.text}</Typography>

                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            mt: 0.5,
                            gap: 0.5
                        }}>
                            <Typography variant="caption" sx={{ color: msg.isOwn ? 'primary.contrastText' : 'text.secondary', opacity: 0.7 }}>
                                {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                            {msg.isOwn && (
                                <DoneAllIcon
                                    fontSize="small"
                                    sx={{
                                        fontSize: '0.9rem',
                                        // If background is blue (primary.main), we need ticks to be visible.
                                        // Unread (Gray equivalent? Or just dimmed white?)
                                        // Read (Bright Blue or White?)
                                        // WhatsApp on dark bg (green):
                                        // Unread: Grayish.
                                        // Read: Blue.
                                        // On primary main (often blue), Blue ticks might blend in.
                                        // Let's assume primary.contrastText is white.
                                        // Unread: rgba(255,255,255, 0.5) (Dimmed)
                                        // Read: #00E5FF (Cyan / Bright Blue) to stand out against Blue background.
                                        color: msg.isRead ? '#00E5FF' : 'rgba(255,255,255,0.5)'
                                    }}
                                />
                            )}
                        </Box>
                    </Paper>
                </Box>
            );
        });
        return result;
    };


    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: { width: 350, maxWidth: '100%', display: 'flex', flexDirection: 'column' }
            }}
        >
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {activeChat ? (
                        <IconButton onClick={handleBackInfo} size="small">
                            <ArrowBackIcon />
                        </IconButton>
                    ) : (
                        <ChatIcon color="primary" />
                    )}
                    <Typography variant="h6" fontWeight="bold">
                        {activeChat ? activeChat.name : (t('common.actions.chat') || "Chat")}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box
                sx={{ flexGrow: 1, overflowY: 'auto', bgcolor: 'background.default', p: 0 }}
                ref={scrollContainerRef}
                onScroll={activeChat ? handleScroll : undefined}
            >
                {!activeChat ? (
                    // User List
                    <List>
                        {users.map((user) => (
                            <React.Fragment key={user.id}>
                                <ListItem button onClick={() => handleUserClick(user)}>
                                    <ListItemAvatar>
                                        <Badge
                                            overlap="circular"
                                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                            variant="dot"
                                            color={user.online ? 'success' : 'default'}
                                        >
                                            <Avatar>{user.name[0]}</Avatar>
                                        </Badge>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={user.name}
                                        secondary={user.online ? 'Online' : 'Offline'}
                                    />
                                    {/* Unread Badge for User */}
                                    {unreadCounts[user.id] > 0 && (
                                        <Badge
                                            badgeContent={unreadCounts[user.id]}
                                            color="primary"
                                            sx={{ mr: 2 }}
                                        />
                                    )}
                                </ListItem>
                                <Divider component="li" />
                            </React.Fragment>
                        ))}
                    </List>
                ) : (
                    // Conversation View
                    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {loadingMore && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
                                <CircularProgress size={20} />
                            </Box>
                        )}
                        {renderMessagesWithDateSeparators()}
                    </Box>
                )}
            </Box>

            {activeChat && (
                <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder={t('common.placeholder.message') || "Type a message..."}
                            value={messageInput}
                            onChange={(e) => setMessageInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                        />
                        <IconButton color="primary" onClick={handleSend} disabled={!messageInput.trim()}>
                            <SendIcon />
                        </IconButton>
                    </Box>
                </Box>
            )}
        </Drawer>
    );
};

export default ChatDrawer;
